import { Box, Button, Chip, CircularProgress, Stack, Tooltip, Typography } from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { ACCENT } from './offerteUi';

// Kleuren per badge-severity — bewust zacht: dit is een statusregel, geen alarmbalk.
const SEV = {
  warning: { bg: '#fef3c7', color: '#92400e' },
  info: { bg: '#dbeafe', color: '#1e40af' },
  success: { bg: '#dcfce7', color: '#166534' },
  error: { bg: '#fee2e2', color: '#991b1b' },
};

const ACTION_ICONS = {
  gripp: <ReceiptLongIcon sx={{ fontSize: 18 }} />,
  check: <FactCheckIcon sx={{ fontSize: 18 }} />,
  openGripp: <OpenInNewIcon sx={{ fontSize: 18 }} />,
  volgende: <AddIcon sx={{ fontSize: 18 }} />,
  genLines: <AutoAwesomeIcon sx={{ fontSize: 18 }} />,
  kiesKlant: null,
};

// Primaire actie groen zodra 'ie naar Gripp schrijft (zoals de oude hoofdknop),
// anders het offerte-indigo.
const primaryColors = (key) => (key === 'gripp'
  ? { bgcolor: '#059669', '&:hover': { bgcolor: '#047857' } }
  : { bgcolor: ACCENT.main, '&:hover': { bgcolor: ACCENT.dark } });

/**
 * De statusregel bovenaan het offerte-document:
 * `● Concept · 6 regels · € 8.450 · [badges…]  [secundair] [PRIMAIR]`
 *
 * Vervangt de stapel Alerts: elke badge klikt door naar de plek waar je het
 * oplost (onBadgeClick). Het "wat nu?" komt uit offerteFlow.nextAction — deze
 * component beslist niets zelf.
 */
function OfferteStatusRegel({ action, linesCount, total, onAction, onBadgeClick, extraActions = null }) {
  if (!action) return null;
  const { statusDot, primary, secondary, badges } = action;

  const renderActionButton = (a, variant) => {
    const busyLabel = a.busy ? (a.busyLabel || 'Bezig…') : null;
    const btn = (
      <Button
        key={a.key}
        variant={variant}
        size={variant === 'contained' ? 'medium' : 'small'}
        disabled={!!a.disabled || !!a.busy}
        onClick={() => onAction(a.key)}
        startIcon={a.busy ? <CircularProgress size={15} color="inherit" /> : ACTION_ICONS[a.key] || null}
        sx={{
          textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap',
          ...(variant === 'contained' ? primaryColors(a.key) : { color: '#475569' }),
        }}
      >
        {busyLabel || a.label}
      </Button>
    );
    // Een uitgeschakelde knop altijd mét uitleg — nooit stil grijs.
    return a.disabled && a.reason
      ? <Tooltip key={a.key} title={a.reason}><span>{btn}</span></Tooltip>
      : btn;
  };

  return (
    <Stack direction="row" alignItems="center" flexWrap="wrap" useFlexGap gap={1}
      sx={{ pb: 1.5, mb: 2, borderBottom: '1px solid #eef2f7' }}>
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
        <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: statusDot.color }} />
        <Typography variant="body2" fontWeight={800} sx={{ color: '#1f2937' }}>{statusDot.label}</Typography>
      </Stack>
      <Typography variant="body2" sx={{ color: '#94a3b8' }}>·</Typography>
      <Typography variant="body2" sx={{ color: '#475569' }}>
        {linesCount} {linesCount === 1 ? 'regel' : 'regels'}
      </Typography>
      <Typography variant="body2" sx={{ color: '#94a3b8' }}>·</Typography>
      <Typography variant="body2" fontWeight={700} sx={{ color: '#1f2937' }}>
        € {Number(total || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2 })}
      </Typography>

      {badges.map((b) => {
        const sev = SEV[b.severity] || SEV.info;
        return (
          <Chip key={b.key} size="small" clickable onClick={() => onBadgeClick(b.key)}
            label={b.label}
            sx={{ bgcolor: sev.bg, color: sev.color, fontWeight: 700, height: 22, fontSize: 11.5 }} />
        );
      })}

      <Box sx={{ flex: 1 }} />
      {extraActions}
      {(secondary || []).map((a) => renderActionButton(a, 'text'))}
      {primary && primary.key !== 'antwoord' && renderActionButton(primary, 'contained')}
    </Stack>
  );
}

export default OfferteStatusRegel;
